import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ReferralProvider } from '@/types/referrals';
import type { UseFormReturn } from 'react-hook-form';
import type { Fields } from './ReferralDeliveryPanel.types';
import { ReferralRecipientSearch } from './ReferralRecipientSearch';

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  disabled: boolean;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}
const SelectField = ({ id, label, value, disabled, options, onChange }: SelectFieldProps) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Select value={value} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={`Veldu ${label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
interface Props {
  id: string;
  disabled: boolean;
  form: UseFormReturn<Fields>;
  fields: Fields;
  rows: ReferralProvider[];
  loading: boolean;
}
export const ReferralRecipientFields = ({ id, disabled, form, fields, rows, loading }: Props) => {
  const set = (key: keyof Fields, value: Fields[keyof Fields]) =>
    form.setValue(key, value, { shouldDirty: true, shouldValidate: true });
  const services = [
    ...new Set(rows.filter((row) => row.id === fields.service_provider_id).map((row) => row.service_name)),
  ];
  return (
    <fieldset disabled={disabled} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <ReferralRecipientSearch
        id={`${id}-recipient`}
        disabled={disabled}
        loading={loading}
        rows={rows}
        value={fields.service_provider_id}
        onSelect={(value) => {
          set('service_provider_id', value);
          const available = [...new Set(rows.filter((row) => row.id === value).map((row) => row.service_name))];
          if (!available.includes(fields.service_name)) set('service_name', available.length === 1 ? available[0] : '');
        }}
      />
      <SelectField
        id={`${id}-service`}
        label="Þjónusta"
        value={fields.service_name}
        disabled={disabled || !fields.service_provider_id}
        options={services.map((value) => ({ value, label: value }))}
        onChange={(value) => set('service_name', value)}
      />
      <SelectField
        id={`${id}-type`}
        label="Tegund beiðni"
        value={fields.service_type}
        disabled={disabled}
        options={['Greiningarbeiðni', 'Meðferðarbeiðni', 'Þjónustubeiðni'].map((value) => ({ value, label: value }))}
        onChange={(value) => {
          set('service_type', value as Fields['service_type']);
        }}
      />
      <SelectField
        id={`${id}-priority`}
        label="Forgangur"
        value={fields.priority}
        disabled={disabled}
        options={['99', '10', '20', '30', '40'].map((value) => ({
          value,
          label: value === '99' ? 'Ótilgreint' : value,
        }))}
        onChange={(value) => set('priority', value as Fields['priority'])}
      />
      <div>
        <Label htmlFor={`${id}-valid`}>Gildir til</Label>
        <input
          id={`${id}-valid`}
          type="date"
          className="h-10 w-full rounded border bg-background px-2"
          {...form.register('valid_until')}
        />
      </div>
    </fieldset>
  );
};
